// src/Pages/LedgerView.jsx
import { useEffect, useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { apiClient } from "../apiClient";

export default function LedgerView() {
  const [clients, setClients] = useState([]);
  const [masterTotal, setMasterTotal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [appliedRange, setAppliedRange] = useState({ from: "", to: "" });

  const userRole = localStorage.getItem("userRole");

  const fetchLedger = async (range) => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (range.from) params.append("from_date", range.from);
      if (range.to) params.append("to_date", range.to);
      const query = params.toString() ? `?${params.toString()}` : "";

      const data = await apiClient.get(`/ledger/${query}`);
      setClients(data.clients);
      setMasterTotal(data.master_total);
    } catch (err) {
      setError(err.message || "Unable to load ledger data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLedger(appliedRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedRange]);

  const handlePdfExport = () => {
    const doc = new jsPDF();
    let title = "Client Ledger Summary";
    if (appliedRange.from || appliedRange.to) {
      const from = appliedRange.from || "earliest";
      const to = appliedRange.to || "latest";
      title += ` (${from} to ${to})`;
    }
    doc.setFontSize(16);
    doc.text(title, 20, 20);

    const tableData = clients.map(entry => [
      entry.client_name,
      (entry.total_owed_groszy / 100).toFixed(2),
      ((entry.total_delivery_payments_groszy + entry.total_unlinked_income_groszy) / 100).toFixed(2),
      (entry.balance_groszy / 100).toFixed(2),
    ]);

    if (masterTotal) {
      tableData.push([
        masterTotal.client_name,
        (masterTotal.total_owed_groszy / 100).toFixed(2),
        ((masterTotal.total_delivery_payments_groszy + masterTotal.total_unlinked_income_groszy) / 100).toFixed(2),
        (masterTotal.balance_groszy / 100).toFixed(2),
      ]);
    }

    autoTable(doc, {
      head: [["Client", "Total Owed (PLN)", "Total Paid (PLN)", "Balance (PLN)"]],
      body: tableData,
      startY: 30,
      styles: { fontSize: 10, cellPadding: 5 },
      headStyles: { fillColor: [35, 38, 94], textColor: 255 },
      didParseCell: (data) => {
        if (data.column.index === 3 && data.row.section === 'body') {
          const balance = parseFloat(data.cell.text[0]);
          if (balance > 0) {
            data.cell.styles.textColor = [255, 0, 0];
            data.cell.styles.fontStyle = 'bold';
          } else {
            data.cell.styles.textColor = [0, 128, 0];
            data.cell.styles.fontStyle = 'bold';
          }
        }
      }
    });

    doc.save("client_ledger_summary.pdf");
  };

  if (loading) {
    return (
      <div className="p-6 text-center bg-[#F5E9D8] min-h-screen">
        <div className="inline-block animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#F76B4C]"></div>
        <p className="mt-2 text-[#2D2F5E]">Loading ledger...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center bg-[#F5E9D8] min-h-screen">
        <div className="text-red-600 bg-red-50 p-4 rounded border border-red-200">
          ❌ {error}
        </div>
      </div>
    );
  }

  if (userRole !== "admin") {
    return (
      <div className="p-6 text-center bg-[#F5E9D8] min-h-screen">
        <h2 className="text-xl text-red-600">❌ Access Denied</h2>
        <p className="text-[#2D2F5E]">Only admin can view ledger.</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto bg-[#F5E9D8] min-h-screen">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6">
        <h3 className="text-2xl font-bold text-[#2D2F5E]">Client Ledger Summary</h3>
        <button
          onClick={handlePdfExport}
          className="mt-3 sm:mt-0 flex items-center bg-[#F76B4C] hover:bg-[#E65A3C] text-white px-4 py-2 rounded-lg transition shadow"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
          Download PDF
        </button>
      </div>

      <div className="bg-white p-4 rounded-xl shadow-sm border border-[#2D2F5E] border-opacity-20 mb-6">
        <h4 className="font-medium text-[#2D2F5E] mb-2">Filter by Date Range</h4>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <label className="block text-sm text-[#2D2F5E] mb-1">From</label>
            <input
              type="date"
              value={dateRange.from}
              onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
              className="w-full border border-[#2D2F5E] border-opacity-30 rounded px-3 py-1 focus:outline-none focus:ring-1 focus:ring-[#F76B4C]"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm text-[#2D2F5E] mb-1">To</label>
            <input
              type="date"
              value={dateRange.to}
              onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
              className="w-full border border-[#2D2F5E] border-opacity-30 rounded px-3 py-1 focus:outline-none focus:ring-1 focus:ring-[#F76B4C]"
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={() => setAppliedRange({ ...dateRange })}
              className="text-sm bg-[#F76B4C] hover:bg-[#E65A3C] text-white px-3 py-1 rounded border border-[#2D2F5E] border-opacity-20"
            >
              Go
            </button>
            <button
              onClick={() => { setDateRange({ from: "", to: "" }); setAppliedRange({ from: "", to: "" }); }}
              className="text-sm bg-[#F5E9D8] hover:bg-[#E0D3C0] text-[#2D2F5E] px-3 py-1 rounded border border-[#2D2F5E] border-opacity-20"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {clients.length === 0 ? (
        <div className="text-center py-8 text-[#2D2F5E] italic">
          No client records found for this period.
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-[#2D2F5E] border-opacity-20 overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-[#2D2F5E] text-[#F5E9D8]">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold uppercase tracking-wider">Client</th>
                <th className="px-4 py-3 text-right text-sm font-semibold uppercase tracking-wider">Total Owed (PLN)</th>
                <th className="px-4 py-3 text-right text-sm font-semibold uppercase tracking-wider">Total Paid (PLN)</th>
                <th className="px-4 py-3 text-right text-sm font-semibold uppercase tracking-wider">Balance (PLN)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#2D2F5E]/10">
              {clients.map((entry) => (
                <tr key={entry.client_id} className="hover:bg-[#F5E9D8] transition">
                  <td className="px-4 py-3 whitespace-nowrap text-[#2D2F5E]">{entry.client_name}</td>
                  <td className="px-4 py-3 text-right text-[#2D2F5E] font-medium">
                    {(entry.total_owed_groszy / 100).toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-right text-[#2D2F5E] font-medium">
                    {((entry.total_delivery_payments_groszy + entry.total_unlinked_income_groszy) / 100).toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-right font-bold">
                    {entry.balance_groszy > 0 ? (
                      <span className="text-red-600">{(entry.balance_groszy / 100).toFixed(2)}</span>
                    ) : (
                      <span className="text-green-600">{(entry.balance_groszy / 100).toFixed(2)}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            {masterTotal && (
              <tfoot className="bg-[#2D2F5E]/10 font-bold">
                <tr>
                  <td className="px-4 py-3 text-[#2D2F5E]">{masterTotal.client_name}</td>
                  <td className="px-4 py-3 text-right text-[#2D2F5E]">
                    {(masterTotal.total_owed_groszy / 100).toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-right text-[#2D2F5E]">
                    {((masterTotal.total_delivery_payments_groszy + masterTotal.total_unlinked_income_groszy) / 100).toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {masterTotal.balance_groszy > 0 ? (
                      <span className="text-red-600">{(masterTotal.balance_groszy / 100).toFixed(2)}</span>
                    ) : (
                      <span className="text-green-600">{(masterTotal.balance_groszy / 100).toFixed(2)}</span>
                    )}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}